import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, CreditCard, Smartphone, Landmark, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loading } from "@/components/auth/RequireAuth";
import { FeeBreakdown } from "@/components/BookingCard";
import { useApi, errMsg, pkr, dateRange } from "@/lib/api";

const METHODS = [["card", "Debit / credit card", CreditCard], ["jazzcash", "JazzCash wallet", Smartphone], ["easypaisa", "Easypaisa wallet", Smartphone], ["bank_transfer", "Bank transfer (IBFT)", Landmark]];

export default function Checkout() {
  const { id } = useParams();
  const api = useApi();
  const [data, setData] = useState(null);
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get(`/bookings/${id}/checkout`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [api, id]);

  const pay = async () => {
    setBusy(true);
    try { const { data: r } = await api.post(`/bookings/${id}/pay`, { method }); setDone(r); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return <Loading />;
  const { booking: b, listing: l } = data;
  if (done || b.status === "confirmed") {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 md:px-8" data-testid="booking-confirmation">
        <CheckCircle2 className="h-10 w-10 text-primary" strokeWidth={1.4} />
        <div className="eyebrow mt-6 text-secondary">Booking confirmed · {b.code}</div>
        <h1 className="mt-2 text-4xl tracking-tight text-primary">You're going to {l.destination}.</h1>
        <p className="mt-4 text-muted-foreground">{l.title}, {dateRange(l.start_date, l.end_date)}. Your payment of {pkr(b.total)} is held by GOSHA and released to the host only after delivery is verified.</p>
        {done && <p className="mt-2 text-xs text-muted-foreground">Payment reference: <span data-testid="payment-ref">{done.payment_ref}</span></p>}
        <Button asChild className="mt-8 rounded-full px-7" data-testid="open-guest-portal-btn"><Link to={`/trips/${b.id}`}>Open your guest portal</Link></Button>
      </div>
    );
  }
  if (b.status !== "pending_payment") return <div className="mx-auto max-w-2xl px-6 py-20"><h1 className="text-3xl">This booking can't be paid</h1><p className="text-muted-foreground">Status: {b.status}</p></div>;
  return (
    <div className="mx-auto grid max-w-5xl gap-10 px-5 py-14 md:px-8 lg:grid-cols-[1fr_360px]" data-testid="checkout-page">
      <div>
        <div className="eyebrow text-secondary">Checkout · PKR</div>
        <h1 className="mt-2 text-4xl tracking-tight text-primary">Pay for your spot</h1>
        <div className="mt-6 flex gap-3 rounded-lg border border-secondary/30 bg-secondary/5 p-4 text-sm" data-testid="simulated-payment-notice">
          <Info className="h-4 w-4 shrink-0 text-secondary" />Simulated checkout: no real money moves in this preview. The payment gateway connects here later.
        </div>
        <RadioGroup value={method} onValueChange={setMethod} className="mt-8 space-y-3">
          {METHODS.map(([v, label, Icon]) => (
            <Label key={v} htmlFor={`m-${v}`} className={`flex cursor-pointer items-center gap-4 rounded-xl border bg-white p-4 transition-colors ${method === v ? "border-primary" : "border-border"}`} data-testid={`payment-method-${v}`}>
              <RadioGroupItem id={`m-${v}`} value={v} /><Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} /><span className="font-normal">{label}</span>
            </Label>
          ))}
        </RadioGroup>
        <Button className="mt-8 w-full rounded-full bg-secondary py-6 text-base hover:bg-secondary/90" onClick={pay} disabled={busy} data-testid="pay-now-btn">{busy ? "Processing…" : `Pay ${pkr(b.total)}`}</Button>
      </div>
      <aside className="rounded-2xl border border-border bg-white p-6" data-testid="checkout-summary">
        <img src={l.hero_image} alt="" className="aspect-[16/10] w-full rounded-lg object-cover" />
        <div className="mt-4 font-display text-xl">{l.title}</div>
        <div className="text-sm text-muted-foreground">{dateRange(l.start_date, l.end_date)} · {b.kind === "private" ? "Private buy-out" : `${b.seats} seat${b.seats > 1 ? "s" : ""}`}</div>
        <div className="mt-5"><FeeBreakdown q={b} label={b.kind === "private" ? "Private buy-out" : `Experience × ${b.seats}`} /></div>
        <p className="mt-4 text-xs text-muted-foreground">Service fee is non-refundable. Experience price refunds follow the listing's cancellation tiers.</p>
      </aside>
    </div>
  );
}
